import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  Paper,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Circle } from "@mui/icons-material";

interface Assignment {
  id: string;
  title: string;
  date: string;
  role: string;
  status: "assigned" | "available" | "unavailable" | "no-data";
}

const dummyAssignments: Assignment[] = [
  {
    id: "1",
    title: "Samuel Smith Memorial",
    date: "June 16",
    role: "Senior Camera Operator",
    status: "assigned",
  },
  {
    id: "2",
    title: "Angela White Summit",
    date: "June 17",
    role: "Lead Lighting Technician",
    status: "assigned",
  },
  {
    id: "3",
    title: "Michael Johnson Festival",
    date: "June 18",
    role: "Sound Mixer",
    status: "assigned",
  },
];

const statusColors = {
  assigned: "#1976d2",
  available: "#2e7d32",
  unavailable: "#d32f2f",
  "no-data": "#f57c00",
};

const statusLabels = {
  assigned: "Assigned",
  available: "Available",
  unavailable: "Unavailable",
  "no-data": "No Data",
};

export default function SchedulingDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Box
        sx={{
          width: 320,
          backgroundColor: colors.primary[900],
          borderRight: "1px solid #e0e0e0",
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Upcoming Assignments
        </Typography>
        <List sx={{ mb: 3 }}>
          {dummyAssignments.map((assignment) => (
            <ListItem key={assignment.id} sx={{ px: 0, py: 1 }}>
              <Card sx={{ width: "100%", boxShadow: 1 }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#1976d2" }}
                    >
                      {assignment.date.toUpperCase()}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "auto", fontSize: "0.75rem" }}
                    >
                      View details
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {assignment.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {assignment.role}
                  </Typography>
                </CardContent>
              </Card>
            </ListItem>
          ))}
        </List>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Insights
        </Typography>
        <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Typically available weekends
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            • Prefers weekdays
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last sync: 3 days ago from Pixel Pilot
          </Typography>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, p: 3 }}>
        <Paper sx={{ height: "100%", p: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              justifyContent: "center",
              flexWrap: "wrap",
              mt: 2,
            }}
          >
            {Object.entries(statusColors).map(([status, color]) => (
              <Box
                key={status}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Circle sx={{ fontSize: 12, color }} />
                <Typography variant="caption">
                  {statusLabels[status as keyof typeof statusLabels]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
